import React, { useEffect, useRef, useState } from "react";
import {
  correctCustomerImportRow, downloadCustomerImportErrors, getCustomerImport, getCustomerImportAttempts, listCustomerImports,
  runCustomerImport, stageCustomerImport, uploadCustomerImport,
} from "../../../api/customerImportsApi";
import { getSchedulingErrorMessage } from "../../../api/schedulingApi";

const fields = [
  ["name", "Name"], ["phone", "Contact phone"], ["external_id", "External customer ID"],
  ["email", "Email"], ["gender", "Gender"], ["date_of_birth", "Date of birth (YYYY-MM-DD)"],
  ["registration_number", "Registration number"], ["notes", "Notes"],
];
const input = "w-full rounded border border-slate-300 px-3 py-2 text-sm";
const button = "rounded border border-cyan-700 px-3 py-2 text-sm font-semibold text-cyan-900 disabled:opacity-50";
const terminal = (row) => ["succeeded", "skipped"].includes(row.status);

function RowEditor({ row, onSave, onAttempts, onImport, busy, canCreate }) {
  const [values, setValues] = useState(row.mapped_data);
  const [selected, setSelected] = useState(row.selected_existing_customer_id || "");
  const [relationship, setRelationship] = useState(row.relationship || "");
  const [editing, setEditing] = useState(false);
  const immutable = terminal(row);
  return <article className="space-y-3 rounded border border-slate-200 p-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="font-semibold">Row {row.row_number}: {row.mapped_data.name || "Unnamed"} <span className="text-sm font-normal">— {row.status.replaceAll("_", " ")}</span></p>
      <div className="flex gap-2">
        {!immutable && canCreate && <button className={button} disabled={busy} onClick={() => setEditing(!editing)}>{editing ? "Cancel edit" : "Edit / resolve"}</button>}
        {canCreate && ["ready", "failed"].includes(row.status) && <button className={button} disabled={busy} onClick={() => onImport(row)}>Import / retry row</button>}
        <button className={button} disabled={busy} onClick={() => onAttempts(row)}>Attempt history ({row.attempt_count})</button>
      </div>
    </div>
    <p className="text-sm text-slate-600">{row.mapped_data.phone || "No own/contact phone"} · External ID: {row.mapped_data.external_id || "Not provided"}</p>
    {row.customer_id && <p className="text-sm">Customer: {row.customer_id} · {row.outcome_code}</p>}
    {!!row.validation_errors?.length && <ul className="text-sm text-rose-700">{row.validation_errors.map((error, index) => <li key={index}>{error.field}: {error.message}</li>)}</ul>}
    {row.last_error_message && <p className="text-sm text-rose-700">{row.last_error_message}</p>}
    {row.status === "needs_relationship" && <p className="text-sm text-amber-800">Existing customers share this phone. Choose the contact and their relationship to the new customer.</p>}
    <details className="text-sm"><summary>Original CSV row</summary><dl>{Object.entries(row.original_data).map(([key, value]) => <div key={key}><dt className="inline font-semibold">{key}: </dt><dd className="inline break-words">{value}</dd></div>)}</dl></details>
    {editing && <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-2">{fields.map(([key, label]) => <label key={key} className="text-sm">{label}
        <input aria-label={`Row ${row.row_number} ${label}`} className={input} value={values[key] || ""}
          onChange={(event) => {
            setValues({ ...values, [key]: event.target.value || null });
            if (key === "phone") { setSelected(""); setRelationship(""); }
          }} />
      </label>)}</div>
      <label className="block text-sm">Existing contact
        <select aria-label={`Row ${row.row_number} existing contact`} className={input} value={selected} onChange={(event) => { setSelected(event.target.value); setRelationship(""); }}>
          <option value="">No contact selected</option>
          {(row.matching_customers || []).map((customer) => <option key={customer.id} value={customer.id}>{customer.name || customer.id} {customer.registration_number ? `(${customer.registration_number})` : ""}</option>)}
        </select>
      </label>
      {selected && <label className="block text-sm">The selected existing customer is the following of the new customer:
        <select aria-label={`Row ${row.row_number} relationship`} className={input} value={relationship} onChange={(event) => setRelationship(event.target.value)}>
          <option value="">Choose relationship</option>
          <option value="self">Self — same person; skip this row</option>
          <option value="father">Father</option><option value="mother">Mother</option><option value="guardian">Guardian</option>
        </select>
      </label>}
      <p className="text-xs text-slate-600">For a changed phone, save first to refresh matching contacts. Import does not verify phone ownership or grant consent.</p>
      <button className={button} disabled={busy || Boolean(selected) !== Boolean(relationship)} onClick={async () => {
        const saved = await onSave(row, { revision: row.revision, values, selected_existing_customer_id: selected || null, relationship: relationship || null });
        if (saved) setEditing(false);
      }}>Save correction / validate</button>
    </div>}
  </article>;
}

export default function CustomerImports({ botId, canCreate, onClose, onImported }) {
  const [history, setHistory] = useState([]);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [batch, setBatch] = useState(null);
  const [file, setFile] = useState(null);
  const [source, setSource] = useState("cusp_legacy");
  const uploadKey = useRef(null);
  const [columns, setColumns] = useState({});
  const [stripSuffix, setStripSuffix] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const [attempts, setAttempts] = useState(null);
  const act = async (work) => {
    setBusy(true); setError("");
    try { await work(); return true; }
    catch (err) { setError(getSchedulingErrorMessage(err, "Customer import failed.")); return false; }
    finally { setBusy(false); }
  };
  useEffect(() => {
    let active = true;
    setBusy(true);
    listCustomerImports(botId, historyOffset).then((data) => { if (active) setHistory(data); })
      .catch((err) => { if (active) setError(getSchedulingErrorMessage(err, "Could not load import history.")); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [botId, historyOffset]);
  const openBatch = async (id, nextFilter = "", nextOffset = 0) => {
    const data = await getCustomerImport(botId, id, { offset: nextOffset, ...(nextFilter ? { status: nextFilter } : {}) });
    setBatch(data); setFilter(nextFilter); setOffset(nextOffset);
    setColumns(data.column_mapping || {});
    setStripSuffix(Boolean(data.transformation_settings?.strip_case_suffix));
    setAttempts(null);
  };
  return <div role="dialog" aria-modal="true" aria-label="Customer imports" className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/50 p-4">
    <div className="mx-auto max-w-6xl space-y-5 rounded-xl bg-white p-6">
      <header className="flex items-center justify-between gap-4"><h2 className="text-xl font-bold">Customer imports</h2><button className={button} disabled={busy} onClick={onClose}>Close</button></header>
      {error && <p role="alert" className="rounded bg-rose-50 p-3 text-rose-800">{error}</p>}
      {busy && <p role="status">Working… Keep this page open until the current request finishes.</p>}
      {!batch ? <>
        {canCreate && <section className="space-y-3 rounded border p-4">
          <h3 className="font-semibold">1. Upload CSV</h3>
          <p className="text-sm">UTF-8 CSV, up to 2 MB / 2,000 rows. Imports create customers only; appointments are not imported.</p>
          <label className="block text-sm">Source system (use the same value for repeat imports)
            <input aria-label="Source system" className={input} value={source} maxLength={100} onChange={(event) => { setSource(event.target.value); uploadKey.current = null; }} />
          </label>
          <input aria-label="CSV file" type="file" accept=".csv,text/csv" disabled={busy} onChange={(event) => { setFile(event.target.files[0] || null); uploadKey.current = null; }} />
          <button className={button} disabled={busy || !file || !source.trim()} onClick={() => act(async () => {
            if (!uploadKey.current) uploadKey.current = crypto.randomUUID();
            const uploaded = await uploadCustomerImport(botId, file, source, uploadKey.current);
            await openBatch(uploaded.id);
          })}>Upload CSV</button>
        </section>}
        <h3 className="font-semibold">Import history</h3>
        {history.length === 0 && <p>No import batches yet.</p>}
        {history.map((item) => <button key={item.id} className="block w-full rounded border p-3 text-left disabled:opacity-50" disabled={busy} onClick={() => act(() => openBatch(item.id))}>
          <strong>{item.original_filename}</strong> · {item.status.replaceAll("_", " ")} · {new Date(item.created_at).toLocaleString()}
          <p className="text-xs">Uploaded by {item.created_by} · {Object.entries(item.counts || {}).map(([state, count]) => `${state}: ${count}`).join(" · ")}</p>
        </button>)}
        <div className="flex gap-2"><button className={button} disabled={busy || !historyOffset} onClick={() => setHistoryOffset(historyOffset - 50)}>Previous imports</button>
          <button className={button} disabled={busy || history.length < 50} onClick={() => setHistoryOffset(historyOffset + 50)}>More imports</button></div>
      </> : <>
        <button className={button} disabled={busy} onClick={() => act(async () => { setHistory(await listCustomerImports(botId, historyOffset)); setBatch(null); })}>Back to import history</button>
        <h3 className="font-semibold">{batch.original_filename} · {batch.status.replaceAll("_", " ")}</h3>
        <p className="text-sm">Source: {batch.source_system} · Uploaded by {batch.created_by}</p>
        {batch.status === "uploaded" ? <section className="space-y-4">
          <h3 className="font-semibold">2. Map CSV columns</h3>
          <p className="text-sm">Map Name and any other customer fields. Leave appointment-specific columns unmapped. External customer ID should map to CaseNumber for CUSP. Without an external ID, repeat uploads may create duplicates; shared phone numbers always require review.</p>
          <div className="grid gap-3 md:grid-cols-2">{fields.map(([field, label]) => <label key={field} className="text-sm">{label}
            <select aria-label={`Map ${label}`} disabled={busy || !canCreate} className={input} value={columns[field] || ""} onChange={(event) => {
              const next = { ...columns }; if (event.target.value) next[field] = event.target.value; else delete next[field]; setColumns(next);
            }}>
              <option value="">Do not import</option>{(batch.file_preview?.headers || []).map((header) => <option key={header} value={header}>{header}</option>)}
            </select>
          </label>)}</div>
          <label className="block text-sm"><input type="checkbox" checked={stripSuffix} disabled={!canCreate || busy} onChange={(event) => setStripSuffix(event.target.checked)} /> Remove trailing numeric case suffix from names, e.g. (252645)</label>
          <details><summary>CSV sample</summary><pre className="overflow-auto text-xs">{JSON.stringify(batch.file_preview?.sample, null, 2)}</pre></details>
          {canCreate && <button className={button} disabled={busy || !columns.name} onClick={() => act(async () => {
            await stageCustomerImport(botId, batch.id, { columns, strip_case_suffix: stripSuffix });
            await openBatch(batch.id);
          })}>3. Prepare rows for review</button>}
        </section> : <>
          <h3 className="font-semibold">4. Review, import, and retry</h3>
          <p className="text-sm">Total: {batch.total} · {Object.entries(batch.counts).map(([state, count]) => `${state.replaceAll("_", " ")}: ${count}`).join(" · ")}</p>
          <p className="text-sm">Self skips the row. Father/mother/guardian creates a separate customer linked to the selected existing contact. Successful rows are never rerun.</p>
          <div className="flex flex-wrap gap-3">
            <select aria-label="Filter import rows" className={input + " sm:w-64"} value={filter} disabled={busy} onChange={(event) => act(() => openBatch(batch.id, event.target.value))}>
              <option value="">All rows</option>{["ready", "needs_relationship", "invalid", "failed", "succeeded", "skipped"].map((state) => <option key={state} value={state}>{state.replaceAll("_", " ")}</option>)}
            </select>
            <button className={button} disabled={busy} onClick={() => act(() => openBatch(batch.id, filter, offset))}>Refresh</button>
            <button className={button} disabled={busy} onClick={() => act(async () => {
              const blob = await downloadCustomerImportErrors(botId, batch.id);
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url; link.download = "customer-import-errors.csv";
              document.body.appendChild(link);
              try { link.click(); } finally { link.remove(); URL.revokeObjectURL(url); }
            })}>Download unresolved rows</button>
            {canCreate && <button className={button} disabled={busy || !(batch.counts.ready || batch.counts.failed)} onClick={() => act(async () => {
              await runCustomerImport(botId, batch.id);
              await openBatch(batch.id, filter, offset);
              onImported?.();
            })}>Import / retry next 25 ready or failed rows</button>}
          </div>
          {(batch.rows || []).map((row) => <RowEditor key={`${row.id}:${row.revision}:${row.status}`} row={row} busy={busy} canCreate={canCreate}
            onImport={(target) => act(async () => {
              await runCustomerImport(botId, batch.id, [target.id]);
              await openBatch(batch.id, filter, offset);
              onImported?.();
            })}
            onSave={(target, body) => act(async () => {
              await correctCustomerImportRow(botId, batch.id, target.id, body);
              await openBatch(batch.id, filter, offset);
            })}
            onAttempts={(target) => act(async () => setAttempts({ row: target.row_number, items: await getCustomerImportAttempts(botId, batch.id, target.id) }))} />)}
          {batch.rows.length === 0 && <p>No rows match this filter.</p>}
          <div className="flex gap-2">
            <button className={button} disabled={busy || offset === 0} onClick={() => act(() => openBatch(batch.id, filter, offset - 50))}>Previous rows</button>
            <button className={button} disabled={busy || batch.rows.length < 50} onClick={() => act(() => openBatch(batch.id, filter, offset + 50))}>Next rows</button>
          </div>
          {attempts && <section className="space-y-2 rounded border p-4"><h3 className="font-semibold">Row {attempts.row} attempt history</h3>
            {attempts.items.length === 0 && <p>No processing attempts yet. Validation and unresolved relationships do not create customers.</p>}
            {attempts.items.map((attempt) => <details key={attempt.id}><summary>Attempt {attempt.attempt_number}: {attempt.status} · {new Date(attempt.started_at).toLocaleString()}</summary>
              <pre className="overflow-auto text-xs">{JSON.stringify(attempt, null, 2)}</pre></details>)}
          </section>}
        </>}
      </>}
    </div>
  </div>;
}
